import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useTransition,
  Suspense,
  lazy,
} from 'react'
import PropTypes from 'prop-types'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Sidebar from 'react-sidebar'

import { LicenseManager, AllEnterpriseModule } from 'ag-grid-enterprise'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import { Auth } from 'aws-amplify'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalytics } from '@/contexts/AnalyticsProvider'
import { useSidebarUI } from '@/contexts/sidebar-ui-context'

// Config imports
import environment from '_environments'
import { apis } from '@/config/apiConfig'
import { keys } from '@/config/keyConfig'
import { apiFetch } from '@/utilities/api'
import * as utils from '@/utilities/util'

// Import hooks
import { queryClientManager } from '@/utilities/queryClientManager'
import { useUser, useUserSession, defaultUser } from '@/queries/hooks/use-user'
import { useMapUrlManager } from '@/utilities/Hooks/useMapUrlManager'
import { usePageTitle } from '@/hooks/usePageTitle'

// Action Handlers
import { Alert as _Alert } from '@/actions'

// Import non-lazy components
import { useDataConfig } from '@/contexts/DataConfigProvider'
import { createCredentials } from '@/utilities/user'
import ImpersonationBanner from '@/components/ImpersonationBanner'
import VersionNotificationModal from '@/components/VersionNotificationModal'
import { UpdateNotificationBanner } from '@/components/UpdateNotificationBanner'
import { ReleasedWidget } from '@/containers/MapControls/Released'
import { useUserImpersonation } from '@/utilities/Hooks/useUserImpersonation'
import { isChunkLoadError } from '@/utilities/runtime/versionDetection'
import Footer from '../Footer/Footer'
import Login from '../Login/Login'
import Reset from '../Login/Reset'
import Verify from '../Login/Verify'
import Loading from './Loading'
import ErrorBoundary from './ErrorBoundary'
import ErrorFallback from './ErrorFallback'
import ChunkErrorFallback from './ChunkErrorFallback'
import Error404 from '../Error404'
import ProfileSidebar from '../Profile/Sidebar'

// Styles
import scss from './App.scss'

// Lazy load route components
const Map = lazy(() => import('../Map/Map'))
const Debug = lazy(() => import('../Profile/Debug'))
const Profile = lazy(() => import('../Profile/Profile'))
const Layers = lazy(() => import('../Profile/Layers'))
const Maps = lazy(() => import('../Profile/Maps'))
const Users = lazy(() => import('../Profile/Users'))
const Accounts = lazy(() => import('../Profile/Accounts'))
const UsageDashboard = lazy(() => import('../Profile/UsageDashboard'))
const Toc = lazy(() => import('../Toc/Toc'))

// Route configurations
const ROUTES = {
  HOME: '/',
  DEBUG: '/debug',
  PROFILE: '/profile',
  LAYERS: '/layers',
  MAPS: '/maps',
  USERS: '/users',
  ACCOUNTS: '/accounts',
  USAGE: '/usage',
  LOGIN: '/login',
  LOGOUT: '/logout',
  RESET: '/reset',
  VERIFY: '/verify',
}

// Sections that render the profile sidebar when logged in and fall back to the
// login form when logged out. A single list keeps both behaviors in sync so a
// section can never render the profile chrome without also being gated to login
// when signed out (replaces the two divergent v5 regex routes).
const PROFILE_SECTION_PATHS = [
  'debug',
  'profile',
  'layers',
  'maps',
  'teams',
  'users',
  'accounts',
  'usage',
]

// Protected route component — derives auth from the user query directly so
// callers don't have to drill `isLoggedIn` through every route element.
const ProtectedRoute = ({ children }) => {
  const location = useLocation()
  const { data: user } = useUser()

  if (user?.isLoggedIn) {
    return children
  }

  return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
}

const App = () => {
  const [isPending] = useTransition()
  const dispatch = useDispatch()

  // Session management with React Query
  const { isLoading: isSessionLoading } = useUserSession()

  // User data with session-aware fetching
  const {
    data: user,
    refetch: refetchUser,
    isLoading: userIsLoading,
  } = useUser()
  const { identifyUser, trackEvent, resetUser } = useAnalytics()
  const { isImpersonating } = useUserImpersonation()

  const dataConfig = useSelector(state => state.updateDataConfig)
  const accountKey = useSelector(state => state.accountKey)
  useMapUrlManager()
  const { tocCollapsed, profileOpen, transitions, openProfile, closeProfile } =
    useSidebarUI()
  const { loadDataConfig } = useDataConfig()

  // Update page title dynamically based on current user, account, and map
  usePageTitle()

  const [qaAccessDenied, setQaAccessDenied] = useState(false)
  const [appState, setAppState] = useState({
    isAuthenticating: false, // Set to false since UserFetchProvider handles session checking
    fetchingDataConfig: false,
    helpWidgetActive: false,
  })

  // Shared function for authentication flow
  const authenticateAndIdentifyUser = useCallback(
    async (
      errorContext = 'Authentication',
      workosProvisioningCredentials = null
    ) => {
      try {
        // First create credentials, then fetch user data
        // Sequential approach is safer for auth flow
        await createCredentials()

        // Now fetch user data
        const { data: userData } = await refetchUser()

        // Validate user data
        if (
          (errorContext === 'Authentication' &&
            Object.keys(userData).length === 0) ||
          (errorContext === 'Login' && !Object.keys(userData).length > 0) ||
          !userData?.UUID
        ) {
          throw new Error('Invalid user data received')
        }

        // QA environment access gate — deny users without canAccessQA on staging
        if (environment.isStaging && userData.canAccessQA !== true) {
          trackEvent('auth_qa_access_denied', {
            email: userData.profile?.emailAddress,
          })
          resetUser()
          await Auth.signOut()
          queryClientManager.setUserData(defaultUser())
          queryClientManager.invalidateUserQueries()
          setQaAccessDenied(true)
          return
        }

        // Identify user in analytics tools
        identifyUser(userData)

        if (errorContext === 'Login' && workosProvisioningCredentials) {
          void apiFetch(
            `${apis.apiDatabase.uri}auth/success`,
            'POST',
            workosProvisioningCredentials
          ).catch(error => {
            console.error('WorkOS migration request failed:', error)
          })
        }
      } catch (error) {
        const msg = String(error?.message ?? error)
        if (msg.includes('No current user')) {
          console.warn('No current user - expected when not logged in')
        } else {
          console.error(`${errorContext} error:`, error)
          utils.showMessage(`${errorContext} Error`, error?.message)
          // Set logged-out state on authentication failure
          queryClientManager.setUserData(defaultUser())
          queryClientManager.invalidateUserQueries()
        }
      }
    },
    [refetchUser, identifyUser, trackEvent, resetUser]
  )

  // Initialize Google Maps and other services in parallel (performance optimization)
  useEffect(() => {
    const initializeServices = async () => {
      try {
        // Check if Google Maps script already exists
        const existingScript = document.querySelector(
          'script[src*="maps.googleapis.com"]'
        )
        if (!existingScript) {
          // Load Google Maps script asynchronously (non-blocking)
          const script = document.createElement('script')
          script.src = `https://maps.googleapis.com/maps/api/js?key=${keys.google}&libraries=places&loading=async`
          script.async = true
          script.defer = true
          script.onload = () => {
            console.log('Google Maps API loaded successfully')
          }
          script.onerror = () => {
            console.warn('Failed to load Google Maps API')
          }
          document.head.append(script)
        }

        // Initialize AG Grid modules and license
        ModuleRegistry.registerModules([
          AllCommunityModule,
          AllEnterpriseModule,
        ])
        LicenseManager.setLicenseKey(keys.agGrid)
      } catch (error) {
        console.error('Service initialization failed:', error)
        dispatch(_Alert({ error: 'Failed to initialize services' }))
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initializeServices()
  }, [dispatch])

  // Single authentication effect - only run when user is logged in
  useEffect(() => {
    const authenticateUser = async () => {
      try {
        await authenticateAndIdentifyUser('Authentication')
      } finally {
        setAppState(prev => ({ ...prev, isAuthenticating: false }))
      }
    }

    // Only run auth if user is logged in
    if (user?.isLoggedIn) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      authenticateUser()
    }
  }, [authenticateAndIdentifyUser, user?.isLoggedIn])

  // Handle data config updates (optimized for earlier loading)
  const shouldLoadDataConfig = useMemo(() => {
    return (
      !appState.fetchingDataConfig &&
      user?.isLoggedIn &&
      user?.mapID &&
      !dataConfig // Only load if we don't have data config yet
    )
  }, [appState.fetchingDataConfig, user?.isLoggedIn, user?.mapID, dataConfig])

  useEffect(() => {
    if (!shouldLoadDataConfig) return
    setAppState(prev => ({ ...prev, fetchingDataConfig: true }))
    loadDataConfig({ updateStyle: false })
      .catch(error => {
        console.error('Error loading data config:', error)
        dispatch(_Alert({ error: 'Failed to load data configuration' }))
      })
      .finally(() => {
        setAppState(prev => ({ ...prev, fetchingDataConfig: false }))
      })
  }, [shouldLoadDataConfig, loadDataConfig, dispatch])

  const handleLogout = useCallback(async () => {
    try {
      await Auth.signOut()
      // Immediately set logged-out state, then invalidate to trigger refetch
      queryClientManager.setUserData(defaultUser())
      queryClientManager.invalidateUserQueries()
    } catch (error) {
      console.error('Logout failed:', error)
      utils.showMessage('Logout Failed', error.message)
    }
  }, [])

  const handleLoginAttempt = useCallback(
    async (
      success = false,
      _userPayload = '',
      workosProvisioningCredentials = null
    ) => {
      if (success) {
        // Invalidate session to trigger refetch
        queryClientManager.invalidateUserQueries()
        await authenticateAndIdentifyUser(
          'Login',
          workosProvisioningCredentials
        )
      }
    },
    [authenticateAndIdentifyUser]
  )

  const getMapUi = useCallback(
    config => {
      const tocWidth = tocCollapsed ? '72px' : 'auto'

      const bannerHeight = '38px'

      return (
        <div className={scss.sidebarHolder}>
          <Sidebar
            sidebar={<ProfileSidebar logout={handleLogout} />}
            transitions={transitions}
            open={profileOpen}
            onSetOpen={open => (open ? openProfile() : closeProfile())}
            styles={{
              root: {
                zIndex: 2,
                top: isImpersonating ? bannerHeight : '0px',
              },
              sidebar: {
                background: '#1A2937',
                borderRadius: '0px 10px 10px 0px',
              },
            }}
          >
            <div
              style={{
                position: 'relative',
                gridTemplateColumns: `${tocWidth} 1fr`,
                height: isImpersonating
                  ? `calc(100vh - ${bannerHeight})`
                  : '100vh',
              }}
              className={scss.container}
            >
              <Routes>
                <Route path={ROUTES.HOME} element={<Toc />} />
                {PROFILE_SECTION_PATHS.map(path => (
                  <Route
                    key={path}
                    path={`/${path}/*`}
                    element={<ProfileSidebar logout={handleLogout} />}
                  />
                ))}
                <Route path='*' element={<Error404 type='map' />} />
              </Routes>

              <main id='main' className={scss.main}>
                <Suspense fallback={<Loading />}>
                  <Routes>
                    <Route
                      path={ROUTES.DEBUG}
                      element={
                        <ProtectedRoute>
                          <Debug />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={ROUTES.PROFILE}
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={ROUTES.LAYERS}
                      element={
                        <ProtectedRoute>
                          <Layers />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={ROUTES.MAPS}
                      element={
                        <ProtectedRoute>
                          <Maps />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={ROUTES.USERS}
                      element={
                        <ProtectedRoute>
                          <Users />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={ROUTES.ACCOUNTS}
                      element={
                        <ProtectedRoute>
                          <Accounts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={ROUTES.USAGE}
                      element={
                        <ProtectedRoute>
                          <UsageDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='*'
                      element={
                        <Map
                          key={accountKey}
                          dataConfig={config}
                          noPreferences='noPreferences'
                        />
                      }
                    />
                  </Routes>
                </Suspense>
              </main>
              <footer id='footer' className={scss.footer}>
                <Footer />
              </footer>
            </div>
          </Sidebar>
        </div>
      )
    },
    [
      tocCollapsed,
      handleLogout,
      accountKey,
      isImpersonating,
      profileOpen,
      transitions,
      openProfile,
      closeProfile,
    ]
  )

  const getLoginUi = useCallback(() => {
    if (appState.isAuthenticating) return <Loading />

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '100vh',
          padding: '24px',
        }}
      >
        <Routes>
          <Route
            path={ROUTES.HOME}
            element={<Login handleLoginAttempt={handleLoginAttempt} />}
          />
          <Route
            path={ROUTES.LOGIN}
            element={<Login handleLoginAttempt={handleLoginAttempt} />}
          />
          <Route
            path={ROUTES.LOGOUT}
            element={<Login handleLoginAttempt={handleLoginAttempt} />}
          />
          <Route
            path={`${ROUTES.RESET}/*`}
            element={<Reset handleLoginAttempt={handleLoginAttempt} />}
          />
          <Route
            path={`${ROUTES.VERIFY}/*`}
            element={<Verify handleLoginAttempt={handleLoginAttempt} />}
          />
          {PROFILE_SECTION_PATHS.map(path => (
            <Route
              key={path}
              path={`/${path}/*`}
              element={<Login handleLoginAttempt={handleLoginAttempt} />}
            />
          ))}
          <Route path='*' element={<Error404 type='login' />} />
        </Routes>
      </div>
    )
  }, [appState.isAuthenticating, handleLoginAttempt])

  const renderContent = useMemo(() => {
    if (qaAccessDenied) {
      return (
        <div className='flex min-h-screen items-center justify-center bg-[#1a2937]'>
          <div className='mx-4 w-full max-w-md rounded-xl border border-white/10 bg-white/5 p-8 text-center'>
            <h2 className='mb-4 text-xl font-semibold text-white'>
              QA Access Required
            </h2>
            <p className='mb-6 text-sm text-gray-300'>
              Your account does not have access to the QA environment. Please
              contact your administrator or use the production application.
            </p>
            <a
              href='https://map.myassetmap.com'
              className='inline-flex items-center justify-center rounded-md bg-[#24c0db] px-6 py-2.5 text-sm font-bold text-black hover:bg-[#4dd4ed]'
            >
              Go to Production
            </a>
          </div>
        </div>
      )
    }

    if (isSessionLoading || userIsLoading || isPending) {
      return <Loading />
    }

    // Only show login if we're done loading and have no user
    if (!user) {
      return getLoginUi()
    }

    if (user.isLoggedIn) {
      if (window.location.pathname === ROUTES.VERIFY) {
        return <Navigate to={ROUTES.HOME} replace />
      }

      if (user.accounts?.length > 0) {
        return getMapUi(dataConfig)
      }

      return (
        <div className='text-center pad-lg'>
          <p>It looks like you are not a part of any accounts.</p>
          <p>
            Please talk to your account administrator and have them add you to
            an account.
          </p>
        </div>
      )
    }

    return getLoginUi()
  }, [
    qaAccessDenied,
    isSessionLoading,
    userIsLoading,
    isPending,
    user,
    dataConfig,
    getLoginUi,
    getMapUi,
  ])

  return (
    <ErrorBoundary
      refreshPage={!environment.isLocal}
      fallbackRender={({ error, rawError, refreshPage }) => {
        if (isChunkLoadError(rawError)) {
          return <ChunkErrorFallback />
        }
        return <ErrorFallback error={error} refreshPage={refreshPage} />
      }}
    >
      {user?.isLoggedIn && <ReleasedWidget />}
      <Suspense fallback={<Loading />}>
        <UpdateNotificationBanner />
        <ImpersonationBanner />
        <VersionNotificationModal
          isLoggedIn={user?.isLoggedIn || false}
          userUUID={user?.UUID}
        />
        {renderContent}
      </Suspense>
    </ErrorBoundary>
  )
}

App.propTypes = {
  // No props - component uses hooks (useSelector, useDispatch) instead of props
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
}

export default App
